/**

// Check if debug mode is enabled (global)
const urlParams = new URLSearchParams(window.location.search);

 * Pagination Controller Module - Управление пагинацией результатов
 * 
 * Phase 5: UI Components Migration
 * Migrated from main.js lines 2315+ (pagination logic)
 */


/**
 * Pagination Controller Class - handles results pagination
 */
class PaginationController {
    constructor() {
        this.pagesSelector = '.pages';
        this.currentPage = 1;
        this.totalItems = 0;
        this.itemsPerPage = 50;
        this.isInitialized = false;
        
        this.defaultConfig = {
            ulClassName: 'pagination justify-content-center',
            prevText: '‹',
            nextText: '›',
            firstText: '«',
            lastText: '»',
            showFirstLast: true,
            showPrevNext: true,
            showPageNumbers: true,
            callback: null
        };
        
        // PaginationController initialization logging only in debug mode
    }
    
    /**
     * Initialize pagination
     * @param {Object} options - Configuration options
     * @returns {boolean} Success status
     */
    initialize(options = {}) {
        try {
            const config = { ...this.defaultConfig, ...options };
            const pagesElement = $(this.pagesSelector);
            
            if (pagesElement.length === 0) {
                if (debugMode === '1') {
                console.warn('[PaginationController] Pages element not found');
            }
                return false;
            }
            
            // Set up configuration
            this.itemsPerPage = config.itemsPerPage || this.itemsPerPage;
            
            // Legacy pagination plugin removed - this is now a stub
            // Pagination is handled by renderFulltextPagination() in app-initialization.search.js
            if (debugMode === '1') {
                console.log('[PaginationController] Legacy initialize called but plugin removed');
            }
            
            // Mark as not initialized since plugin is removed
            this.isInitialized = false;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] Pagination initialized', {
                    config: config,
                    itemsPerPage: this.itemsPerPage
                });
            }
            
            return true;
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error initializing pagination:', error);
            }
            return false;
        }
    }
    
    /**
     * Handle page change event
     * @param {Object} data - Page data
     * @param {Object} pagination - Pagination object
     */
    handlePageChange(data, pagination) {
        try {
            this.currentPage = data.page;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] Page changed', {
                    page: data.page,
                    totalPages: pagination.totalNumber,
                    startIndex: data.startIndex,
                    endIndex: data.endIndex
                });
            }
            
            // Update URL if needed
            this.updateURL(data.page);
            
            // Trigger page change event
            this.triggerPageChangeEvent(data, pagination);
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error handling page change:', error);
            }
        }
    }
    
    /**
     * Update URL with current page
     * @param {number} page - Current page number
     */
    updateURL(page) {
        try {
            if (page <= 1) return;
            
            // Skip URL update if we're restoring state from browser history
            const isRestoring = window.__isRestoringBrowserState === true;
            if (isRestoring) {
                return;
            }
            
            const url = new URL(window.location);
            url.searchParams.set('page', page);
            
            // Use pushState to create history entry for back/forward navigation
            window.history.pushState({}, '', url);
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] URL updated', {
                    page: page,
                    url: url.toString()
                });
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error updating URL:', error);
            }
        }
    }
    
    /**
     * Trigger custom page change event
     * @param {Object} data - Page data
     * @param {Object} pagination - Pagination object
     */
    triggerPageChangeEvent(data, pagination) {
        try {
            const event = new CustomEvent('paginationPageChange', {
                detail: {
                    page: data.page,
                    startIndex: data.startIndex,
                    endIndex: data.endIndex,
                    totalPages: pagination.totalNumber,
                    totalItems: this.totalItems
                }
            });
            
            document.dispatchEvent(event);
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error triggering event:', error);
            }
        }
    }
    
    /**
     * Update pagination with new data
     * @param {number} totalItems - Total number of items
     * @param {Object} options - Update options
     */
    updatePagination(totalItems, options = {}) {
        try {
            this.totalItems = totalItems;
            const totalPages = Math.ceil(totalItems / this.itemsPerPage);
            
            const pagesElement = $(this.pagesSelector);
            
            // Legacy pagination plugin removed - stub method
            if (debugMode === '1') {
                console.log('[PaginationController] updatePagination called but plugin removed');
            }
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] Pagination updated (stub)', {
                    totalItems: totalItems,
                    totalPages: totalPages,
                    itemsPerPage: this.itemsPerPage
                });
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error updating pagination:', error);
            }
        }
    }
    
    /**
     * Go to specific page
     * @param {number} page - Page number to go to
     */
    goToPage(page) {
        try {
            if (page < 1) page = 1;
            
            const pagesElement = $(this.pagesSelector);
            
            // Legacy pagination plugin removed - stub method
            if (debugMode === '1') {
                console.log('[PaginationController] goToPage called but plugin removed');
            }
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] Navigated to page (stub)', {
                    page: page
                });
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error going to page:', error);
            }
        }
    }
    
    /**
     * Go to next page
     */
    nextPage() {
        this.goToPage(this.currentPage + 1);
    }
    
    /**
     * Go to previous page
     */
    prevPage() {
        this.goToPage(this.currentPage - 1);
    }
    
    /**
     * Go to first page
     */
    firstPage() {
        this.goToPage(1);
    }
    
    /**
     * Go to last page
     */
    lastPage() {
        const totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        this.goToPage(totalPages);
    }
    
    /**
     * Set items per page
     * @param {number} itemsPerPage - Number of items per page
     */
    setItemsPerPage(itemsPerPage) {
        try {
            this.itemsPerPage = itemsPerPage;
            
            // Reinitialize pagination with new items per page
            if (this.isInitialized) {
                this.updatePagination(this.totalItems);
            }
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] Items per page changed', {
                    itemsPerPage: itemsPerPage
                });
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error setting items per page:', error);
            }
        }
    }
    
    /**
     * Get current page info
     * @returns {Object} Current page information
     */
    getCurrentPageInfo() {
        const totalPages = Math.ceil(this.totalItems / this.itemsPerPage);
        const startIndex = (this.currentPage - 1) * this.itemsPerPage;
        const endIndex = Math.min(startIndex + this.itemsPerPage - 1, this.totalItems - 1);
        
        return {
            currentPage: this.currentPage,
            totalPages: totalPages,
            totalItems: this.totalItems,
            itemsPerPage: this.itemsPerPage,
            startIndex: startIndex,
            endIndex: endIndex,
            hasNext: this.currentPage < totalPages,
            hasPrev: this.currentPage > 1
        };
    }
    
    /**
     * Destroy pagination
     */
    destroy() {
        try {
            const pagesElement = $(this.pagesSelector);
            
            // Legacy pagination plugin removed - just clear container
            if (pagesElement.length > 0) {
                pagesElement.empty();
            }
            
            this.isInitialized = false;
            this.currentPage = 1;
            this.totalItems = 0;
            
            if (window.HyperLog) {
                window.HyperLog.debug('[PaginationController] Pagination destroyed');
            }
            
        } catch (error) {
            if (debugMode === '1') {
                console.error('[PaginationController] Error destroying pagination:', error);
            }
        }
    }
    
    /**
     * Check if pagination is initialized
     * @returns {boolean} Initialization status
     */
    isInitialized() {
        return this.isInitialized;
    }
    
    /**
     * Get pagination configuration
     * @returns {Object} Current configuration
     */
    getConfiguration() {
        return {
            pagesSelector: this.pagesSelector,
            currentPage: this.currentPage,
            totalItems: this.totalItems,
            itemsPerPage: this.itemsPerPage,
            isInitialized: this.isInitialized,
            defaultConfig: this.defaultConfig
        };
    }
}

// Create singleton instance
const paginationController = new PaginationController();

// Export for use in other modules
export { paginationController };

// Global export for backward compatibility
window.paginationController = paginationController;

// Register with migration controller
//         'initialize',
//         'updatePagination',
//         'goToPage',
//         'nextPage',
//         'prevPage',
//         'firstPage',
//         'lastPage',
//         'setItemsPerPage',
//         'getCurrentPageInfo',
//         'destroy',
//         'getConfiguration'
    
//     // Mark as migrated from main.js
// }

// Module registration logging only in debug mode 